/**
 * Product card behaviour (split-product swatches, collection quick add).
 *
 * Replaces the inline <script> that snippets/product-card-item-new.liquid used to
 * print once per card (~7 KB × every card on the page). One delegated click
 * listener covers every card, including cards injected later by AJAX (Rebuy,
 * recommendations), whose inline scripts never ran.
 *
 * Per-card data comes from the card root: [data-product-id] and
 * [data-unavailable-variants] (comma-separated sold-out variant ids).
 * Depends on the Shopify `meta.products` global and `theme.Cart` (theme.js).
 */
(function () {
  'use strict';

  var CARD_SELECTOR = '.product-item-block[data-product-id]';
  var QUICK_ADD_DELAY_MS = 20;
  var lastQuickAddCall = 0;

  function currentTemplate() {
    var bodyClasses = document.body.classList;
    if (bodyClasses.contains('template-collection')) return 'collection';
    if (bodyClasses.contains('template-search')) return 'search';
    if (bodyClasses.contains('template-product')) return 'product';
    return 'other';
  }

  function isVariantAvailable(card, variantId) {
    var unavailableIds = (card.dataset.unavailableVariants || '').split(',');
    return unavailableIds.indexOf(String(variantId)) === -1;
  }

  function findMetaProduct(card) {
    if (!window.meta || !window.meta.products) return null;
    var productId = card.dataset.productId;
    return window.meta.products.filter(function (product) {
      return String(product.id) === productId;
    })[0] || null;
  }

  /* ---- Split-product swatches (all templates) ---- */

  function handleSplitSwatchClick(card, colorSwatch) {
    var inputs = card.querySelectorAll('input');
    for (var index = 0; index < inputs.length; index++) inputs[index].checked = false;

    var input = colorSwatch.querySelector('input');
    if (!input) return;
    input.checked = true;

    var imageElement = card.querySelector('.product-card__image-wrapper img');
    if (imageElement) imageElement.srcset = input.dataset.productImage;

    var productUrl = input.dataset.productUrl;
    var imageLink = card.querySelector('.product-card__link-image');
    var titleLink = card.querySelector('.product-card__link-title');
    if (imageLink) imageLink.setAttribute('href', productUrl);
    if (titleLink) titleLink.setAttribute('href', productUrl);
  }

  /* ---- Quick add (collection template) ---- */

  // Variant titles look like "Color / Size"; colors may themselves contain " / ".
  // Kept 1:1 with the former inline script: with 3+ parts the color is a
  // substring match on the joined title, with 2 parts it is an exact part match.
  function variantMatchesColor(publicTitle, selectedColor) {
    var parts = (publicTitle || '').split(' / ');
    if (parts.length >= 3) return parts.slice(0, -1).join(' / ').indexOf(selectedColor) !== -1;
    return parts.indexOf(selectedColor) !== -1;
  }

  function variantSize(publicTitle, withColors) {
    var parts = (publicTitle || '').split(' / ');
    if (withColors && parts.length >= 3) return parts[parts.length - 1];
    return parts[1];
  }

  function assignSizeSwatches(sizeSwatches, availableVariants, withColors) {
    for (var index = 0; index < sizeSwatches.length; index++) {
      var sizeSwatch = sizeSwatches[index];
      var match = availableVariants.filter(function (variant) {
        return variantSize(variant.public_title, withColors) == sizeSwatch.dataset.value;
      })[0];
      if (match) {
        sizeSwatch.dataset.variantId = match.id;
        sizeSwatch.classList.remove('sold-out');
      } else {
        sizeSwatch.classList.add('sold-out');
      }
    }
  }

  function handleColorClickForQuickAdd(card, colorSwatch) {
    var product = findMetaProduct(card);
    var sizeSwatches = card.querySelectorAll('.size-swatch');
    if (!product || !sizeSwatches.length) return;

    var input = colorSwatch.querySelector('input');
    var selectedColor = input ? input.value : '';

    var availableVariants = product.variants.filter(function (variant) {
      return variantMatchesColor(variant.public_title, selectedColor) &&
        isVariantAvailable(card, variant.id);
    });
    assignSizeSwatches(sizeSwatches, availableVariants, true);
  }

  function initSizeOnlyCard(card) {
    var product = findMetaProduct(card);
    var sizeSwatches = card.querySelectorAll('.size-swatch');
    if (!product || !sizeSwatches.length) return;
    var availableVariants = product.variants.filter(function (variant) {
      return isVariantAvailable(card, variant.id);
    });
    assignSizeSwatches(sizeSwatches, availableVariants, false);
  }

  function handleSizeClick(sizeSwatch) {
    var variantId = sizeSwatch.dataset.variantId;
    if (!variantId || Date.now() < lastQuickAddCall + QUICK_ADD_DELAY_MS) return;
    lastQuickAddCall = Date.now();

    var sizeSwatches = sizeSwatch.closest('.size-swatches');
    var isFinalSale = sizeSwatches && sizeSwatches.dataset.finalSale === 'true';
    var item = { id: variantId, quantity: 1 };
    if (isFinalSale) item.properties = { _final_sale: 'true' };

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item] })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Request failed.');
        return response.json();
      })
      .then(function () {
        window.theme.Cart.prototype._onProductAdded();
      })
      .catch(function (error) {
        console.error('Error:', error);
      });
  }

  /* ---- Wiring ---- */

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var card = target.closest(CARD_SELECTOR);
    if (!card) return;

    var colorSwatch = target.closest('.color-swatch');
    if (colorSwatch) {
      if (colorSwatch.closest('.split-product-swatch-list')) handleSplitSwatchClick(card, colorSwatch);
      if (currentTemplate() === 'collection') handleColorClickForQuickAdd(card, colorSwatch);
      return;
    }

    var sizeSwatch = target.closest('.size-swatch');
    if (sizeSwatch && currentTemplate() === 'collection') handleSizeClick(sizeSwatch);
  });

  function initCard(card) {
    if (card.dataset.productCardReady) return;
    card.dataset.productCardReady = 'true';

    var template = currentTemplate();
    if (template === 'collection') {
      var hasColors = card.querySelector('.color-swatch');
      if (hasColors) {
        var checkedInput = card.querySelector('input[checked="checked"]');
        if (checkedInput && checkedInput.parentElement) checkedInput.parentElement.click();
      } else {
        initSizeOnlyCard(card);
      }
    } else if (template === 'other') {
      // Layout offset the inline script used to apply outside collection/search/product.
      var gridItem = card.closest('.grid__item');
      if (gridItem) gridItem.style.marginLeft = '10px';
    }
  }

  function initAllCards() {
    var cards = document.querySelectorAll(CARD_SELECTOR);
    for (var index = 0; index < cards.length; index++) initCard(cards[index]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllCards);
  } else {
    initAllCards();
  }
})();
